import { useTranslation } from 'react-i18next';
import { ChevronDown, Info } from 'lucide-react';
import { useAuthStore } from '../../stores/authStore';
import ImageSquare from '../../shared_components/ImageSquare/ImageSquare';
import { storage } from '../../context/firebase';
import { useState } from 'react';
import './MaterialMismatchSection.scss';
import { Tooltip } from '@mui/material';
import {
  getLabelDisplayName,
  getLabelIdMatchingGroup,
  getLabelsetBelongingToGroup,
} from '../../shared_components/utils/FirebaseLabelsetsUtils';
import {
  getMismatchesByMaterial,
  calculateMaterialMismatchCount,
  getBaleIdsByMismatchMaterial,
} from '../../shared_components/utils/DeliveryAnalyticsUtils';

export default function MaterialMismatchSection({ delivery, importedBalesData, showBale }) {
  const { t, i18n } = useTranslation();
  const globalLabelsets = useAuthStore((state) => state.globalLabelsets);
  const [expanded, setExpanded] = useState(false);

  // Get expected material label ID
  const expectedMaterialLabelId = getLabelIdMatchingGroup(delivery?.delivery_labels, globalLabelsets, 'materials');

  // Get mismatches by material type
  const mismatchesByMaterial = getMismatchesByMaterial(delivery, globalLabelsets);

  // Calculate mismatch statistics
  const { mismatchCount, mismatchPercentage, totalCount } = calculateMaterialMismatchCount(delivery, globalLabelsets);

  // Get metadata for display
  const materialsLabelsetId = getLabelsetBelongingToGroup(
    Object.keys(delivery?.delivery_labels || {}),
    globalLabelsets,
    'materials',
  );
  const expectedMaterial = getLabelDisplayName(
    globalLabelsets?.[materialsLabelsetId],
    expectedMaterialLabelId,
    i18n?.language,
  );

  // Get bale IDs grouped by mismatch material
  const baleIdsByMaterial = getBaleIdsByMismatchMaterial(importedBalesData, globalLabelsets, expectedMaterialLabelId);

  // Transform mismatches into array format for rendering
  const mismatchMaterials = Object.entries(mismatchesByMaterial).map(([materialKey, count]) => ({
    materialKey,
    materialName:
      getLabelDisplayName(globalLabelsets?.[materialsLabelsetId], materialKey, i18n?.language) ?? materialKey,
    count,
  }));

  const matchCount = totalCount - mismatchCount;
  const matchPercentage = 100 - mismatchPercentage;

  const isCompleteMatch = mismatchPercentage === 0;

  const tooltipContent = (
    <div style={{ whiteSpace: 'pre-line' }}>
      {t('mismatch_analysis_summary_tooltip', {
        matchCount: matchCount,
        matchPercentage: matchPercentage.toFixed(1),
        mismatchCount: mismatchCount,
        analyzed: totalCount,
      })}
    </div>
  );

  return (
    <div className='delivery--ai-nested-card expandable'>
      <div className='delivery--ai-nested-card-header' onClick={() => setExpanded(!expanded)}>
        <div className='delivery--ai-nested-card-info'>
          <div className='delivery--ai-nested-card-icon-wrapper orange'>
            <Info className='delivery--ai-nested-card-icon' />
          </div>
          <div className='delivery--ai-nested-card-text'>
            <h4 className='delivery--ai-nested-card-title'>
              {t('material_match')}
              <span className='beta-badge'>BETA</span>
            </h4>
            <p className='delivery--ai-nested-card-subtitle'>
              {t('expected')}: {expectedMaterial}
            </p>
          </div>
        </div>
        <div className='delivery--ai-nested-card-value'>
          <div className='delivery--ai-nested-card-main-value'>
            <span>{matchCount}</span>
            <span className='light-text'>/{totalCount}</span>
          </div>
          <span className='delivery--ai-nested-card-trend positive'>{matchPercentage.toFixed(1)}%</span>
        </div>
      </div>
      {expanded && (
        <div className='delivery--ai-nested-card-expanded'>
          {isCompleteMatch ? (
            <div className='delivery--ai-expanded-content'>
              <div className='delivery--ai-error-images-grid'>
                <div className='delivery--ai-no-errors'>
                  <div className='delivery--ai-no-errors-icon'>✓</div>
                  <p>{t('material_mismatch_complete_match_message')}</p>
                </div>
              </div>
            </div>
          ) : (
            <div className='delivery--ai-expanded-content'>
              <Tooltip title={tooltipContent} arrow placement='top'>
                <div className='mismatch-overall-progress-bar-container'>
                  <div className='match-percentage-container'>
                    <div className='percentage-indicator-container'>
                      <span className='green-dot' />
                      <span className='percentage-label'>
                        {t('match')} {matchPercentage.toFixed(1)}%
                      </span>
                    </div>
                    <div className='percentage-indicator-container'>
                      <span className='orange-dot' />
                      <span className='percentage-label'>
                        {t('mismatch')} {mismatchPercentage.toFixed(1)}%
                      </span>
                    </div>
                  </div>
                  <div className='match-overall-progress-bar'>
                    <div className='match-overall-progress-bar-fill' style={{ width: `${matchPercentage}%` }} />
                  </div>
                </div>
              </Tooltip>
              <p className='delivery--ai-mismatch-breakdown-header'>{t('material_mismatch_breakdown')}</p>
              <p className='delivery--ai-mismatch-breakdown-subHeader'>
                {t('material_mismatch_breakdown_description')}
              </p>
              {mismatchMaterials.map(({ materialKey, materialName, count }, index) => {
                const percentage = ((count / mismatchCount) * 100).toFixed(1);
                return (
                  <MismatchMaterial
                    key={materialKey}
                    materialKey={materialKey}
                    materialName={materialName}
                    percentage={percentage}
                    importedBalesData={importedBalesData}
                    baleIds={baleIdsByMaterial[materialKey] || []}
                    count={count}
                    index={index}
                    onImageClick={showBale}
                  />
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const MismatchMaterial = ({ materialName, percentage, importedBalesData, baleIds, count, index, onImageClick }) => {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);

  const handleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  const handleImageClick = (baleId) => {
    if (baleId) {
      onImageClick(baleId, baleIds);
    }
  };

  return (
    <div className='material-mismatch-row-container'>
      <div className='material-mismatch-row' onClick={handleExpand}>
        <div className='material-mismatch-row-left'>
          <span
            className='material-mismatch-row-color-indicator'
            style={{ backgroundColor: colors[index % colors.length] }}
          />
          {materialName}
          <ChevronDown
            size={16}
            className={`icon-chevron-down ${isExpanded ? 'expanded' : ''}`}
            onClick={handleExpand}
          />
        </div>
        <div className='material-mismatch-row-right'>
          <span className='material-mismatch-row-count'>
            {count} {count > 1 ? t('bales') : t('bale')}
          </span>
        </div>
      </div>
      <div className='material-mismatch-row-progress-bar'>
        <div
          className='material-mismatch-row-progress-bar-fill'
          style={{ width: `${percentage}%`, backgroundColor: colors[index % colors.length] }}
        />
      </div>
      {isExpanded && (
        <div className='material-mismatch-row-images-container'>
          {baleIds.map((baleId) => {
            const imagePath = importedBalesData[baleId]?.image_storage_path;
            return (
              <div key={baleId} onClick={() => handleImageClick(baleId)}>
                <ImageSquare storage={storage} imageStoragePath={imagePath} size={70} />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const colors = ['#3b82f6', '#8b5cf6', '#06b6d4'];
